/**
 * Pure logic for the "Who starts?" finger picker (Chwazi-style).
 *
 * Everyone puts a finger on the screen; once the set of fingers has been
 * stable for COUNTDOWN_MS one finger is picked uniformly at random.
 * Adding or lifting a finger restarts the countdown.
 */

export const COUNTDOWN_MS = 3000;
export const MIN_FINGERS = 2;

/**
 * Finger colours, all readable on the dark (stone-900) background with
 * at least 7:1 contrast and spread around the hue wheel.
 * Fingers also carry a number so colour is never the only cue.
 */
export const FINGER_COLORS = [
  '#F87171', // red-400
  '#60A5FA', // blue-400
  '#34D399', // emerald-400
  '#FBBF24', // amber-400
  '#C084FC', // purple-400
  '#F472B6', // pink-400
  '#22D3EE', // cyan-400
  '#A3E635', // lime-400
  '#FB923C', // orange-400
  '#E7E5E4', // stone-200
];

export interface Finger {
  id: number; // PointerEvent.pointerId
  x: number;
  y: number;
  color: string;
  label: number; // 1-based, lowest free number
}

export type PickerPhase = 'waiting' | 'counting' | 'picked';

export interface PickerState {
  phase: PickerPhase;
  fingers: Finger[];
  /** Epoch ms when the pick happens, null when not counting down. */
  deadline: number | null;
  winnerId: number | null;
}

export type PickerAction =
  | { type: 'down'; id: number; x: number; y: number; now: number }
  | { type: 'move'; id: number; x: number; y: number }
  | { type: 'up'; id: number; now: number }
  | { type: 'tick'; now: number; random?: RandomSource }
  | { type: 'reset' };

/** Fills the array with random 32-bit values (crypto.getRandomValues signature). */
export type RandomSource = (array: Uint32Array) => Uint32Array;

const defaultRandom: RandomSource = (array) => crypto.getRandomValues(array);

/**
 * Uniform random integer in [0, n) from a CSPRNG, using rejection sampling
 * so there is no modulo bias.
 */
export function secureRandomInt(n: number, random: RandomSource = defaultRandom): number {
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error(`secureRandomInt needs a positive integer, got ${n}`);
  }
  const range = 0x100000000; // 2^32
  const limit = range - (range % n);
  const buf = new Uint32Array(1);
  // Expected iterations < 2; bounded to avoid an infinite loop on a broken source.
  for (let i = 0; i < 1000; i++) {
    random(buf);
    if (buf[0] < limit) return buf[0] % n;
  }
  throw new Error('Random source kept returning out-of-range values');
}

export function pickRandom<T>(items: T[], random?: RandomSource): T {
  if (items.length === 0) throw new Error('Cannot pick from an empty list');
  return items[secureRandomInt(items.length, random)];
}

/** First palette colour no current finger uses (cycles if all are taken). */
export function nextColor(fingers: Finger[]): string {
  const used = new Set(fingers.map((f) => f.color));
  return FINGER_COLORS.find((c) => !used.has(c)) ?? FINGER_COLORS[fingers.length % FINGER_COLORS.length];
}

/** Lowest positive label no current finger uses. */
export function nextLabel(fingers: Finger[]): number {
  const used = new Set(fingers.map((f) => f.label));
  let label = 1;
  while (used.has(label)) label++;
  return label;
}

function withCountdown(fingers: Finger[], now: number): PickerState {
  const enough = fingers.length >= MIN_FINGERS;
  return {
    phase: enough ? 'counting' : 'waiting',
    fingers,
    deadline: enough ? now + COUNTDOWN_MS : null,
    winnerId: null,
  };
}

export const initialPickerState: PickerState = {
  phase: 'waiting',
  fingers: [],
  deadline: null,
  winnerId: null,
};

export function pickerReducer(state: PickerState, action: PickerAction): PickerState {
  switch (action.type) {
    case 'down': {
      // Once a winner is shown, new touches don't join until "Again".
      if (state.phase === 'picked') return state;
      if (state.fingers.some((f) => f.id === action.id)) return state;
      const finger: Finger = {
        id: action.id,
        x: action.x,
        y: action.y,
        color: nextColor(state.fingers),
        label: nextLabel(state.fingers),
      };
      return withCountdown([...state.fingers, finger], action.now);
    }
    case 'move': {
      if (!state.fingers.some((f) => f.id === action.id)) return state;
      return {
        ...state,
        fingers: state.fingers.map((f) => (f.id === action.id ? { ...f, x: action.x, y: action.y } : f)),
      };
    }
    case 'up': {
      if (!state.fingers.some((f) => f.id === action.id)) return state;
      const fingers = state.fingers.filter((f) => f.id !== action.id);
      if (state.phase === 'picked') {
        // Keep the result on screen; just drop the lifted finger's dot
        // (the winner's dot stays so the result remains visible).
        return action.id === state.winnerId ? state : { ...state, fingers };
      }
      return withCountdown(fingers, action.now);
    }
    case 'tick': {
      if (state.phase !== 'counting' || state.deadline === null) return state;
      if (action.now < state.deadline || state.fingers.length < MIN_FINGERS) return state;
      const winner = pickRandom(state.fingers, action.random);
      return { ...state, phase: 'picked', deadline: null, winnerId: winner.id };
    }
    case 'reset':
      return initialPickerState;
    default:
      return state;
  }
}

/**
 * Starting positions for a live play when `firstTeam` starts: the chosen
 * team is 1 and the rest follow in their existing order, wrapping around.
 */
export function startingPositionsFrom(
  teamNames: string[],
  firstTeam: string
): Record<string, { startingPosition: number }> {
  const start = teamNames.indexOf(firstTeam);
  if (start < 0) throw new Error(`Unknown team ${firstTeam}`);
  const result: Record<string, { startingPosition: number }> = {};
  teamNames.forEach((_, i) => {
    const name = teamNames[(start + i) % teamNames.length];
    result[name] = { startingPosition: i + 1 };
  });
  return result;
}
